import React, { useState, useEffect, useRef } from 'react';
import { Modal, Form, Button, Spinner, Card } from 'react-bootstrap';
import { upsertLessonFull } from '../../../api/lessons';
import './LessonModal.scss';

export const LessonModal = ({ show, onHide, communityId, onSuccess, existingLesson = null }) => {
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [videoLink, setVideoLink] = useState('');
  const [videoFile, setVideoFile] = useState(null);
  
  // For PDFs, we need to track both existing (from backend) and newly uploaded ones.
  // In this task, we focus on creation, but we'll use a unified state if we want to support edits.
  const [pdfs, setPdfs] = useState([]); 
  
  const [questions, setQuestions] = useState([]);
  
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const videoInputRef = useRef(null);
  const pdfInputRef = useRef(null);

  useEffect(() => {
    if (show) {
      // Reset form on show
      setTitle('');
      setDescription('');
      setVideoLink('');
      setVideoFile(null);
      setPdfs([]);
      setQuestions([]);
      setError('');
    }
  }, [show]);

  const handlePdfUpload = (e) => {
    if (e.target.files) {
      const newFiles = Array.from(e.target.files).map(file => ({
        id: Math.random().toString(36).substr(2, 9),
        file,
        name: file.name,
        isNew: true
      }));
      setPdfs(prev => [...prev, ...newFiles]);
    }
  };

  const handleRemovePdf = (id) => {
    setPdfs(prev => prev.filter(p => p.id !== id));
  };

  const handleAddQuestion = () => {
    const newQ = {
      id: Math.random().toString(36).substr(2, 9),
      quizType: 'paragraph',
      question: '',
      paragraphRightAnswer: '',
      options: [
        { id: Math.random().toString(36).substr(2, 9), text: '', isCorrect: false },
        { id: Math.random().toString(36).substr(2, 9), text: '', isCorrect: false }
      ],
      isEditing: true
    };
    setQuestions([...questions, newQ]);
  };

  const updateQuestion = (id, field, value) => {
    setQuestions(prev => prev.map(q => q.id === id ? { ...q, [field]: value } : q));
  };

  const removeQuestion = (id) => {
    setQuestions(prev => prev.filter(q => q.id !== id));
  };

  const addOption = (qId) => {
    setQuestions(prev => prev.map(q => {
      if (q.id === qId) {
        return {
          ...q,
          options: [...q.options, { id: Math.random().toString(36).substr(2, 9), text: '', isCorrect: false }]
        };
      }
      return q;
    }));
  };

  const updateOption = (qId, optId, field, value) => {
    setQuestions(prev => prev.map(q => {
      if (q.id === qId) {
        const newOptions = q.options.map(o => o.id === optId ? { ...o, [field]: value } : o);
        // If singleChoice and field is isCorrect = true, we must unset others
        if (q.quizType === 'singleChoice' && field === 'isCorrect' && value === true) {
          newOptions.forEach(o => {
            if (o.id !== optId) o.isCorrect = false;
          });
        }
        return { ...q, options: newOptions };
      }
      return q;
    }));
  };

  const removeOption = (qId, optId) => {
    setQuestions(prev => prev.map(q => {
      if (q.id === qId) {
        return { ...q, options: q.options.filter(o => o.id !== optId) };
      }
      return q;
    }));
  };

  const toggleEditQuestion = (id) => {
    setQuestions(prev => prev.map(q => q.id === id ? { ...q, isEditing: !q.isEditing } : q));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const formData = new FormData();
      formData.append('title', title);
      formData.append('description', description);
      formData.append('community', communityId);
      
      if (videoLink) {
        formData.append('videoLink', videoLink);
      }
      if (videoFile) {
        formData.append('video', videoFile);
      }

      pdfs.forEach(p => {
        if (p.isNew && p.file) {
          formData.append('pdfs', p.file);
        }
      });

      // Prepare MCQs
      const processedMcqs = questions.map(q => ({
        question: q.question,
        quizType: q.quizType,
        paragraphRightAnswer: q.quizType === 'paragraph' ? q.paragraphRightAnswer : null,
        options: q.quizType !== 'paragraph' ? q.options.map(o => ({
          text: o.text,
          isCorrect: o.isCorrect
        })) : []
      }));

      formData.append('mcqs', JSON.stringify(processedMcqs));

      await upsertLessonFull(formData);
      onSuccess();
      onHide();
    } catch (err) {
      console.error(err);
      setError(err.response?.data?.message || err.message || 'Failed to create course');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal show={show} onHide={onHide} dialogClassName="fullscreen-modal cd-course-modal">
      <div className="cd-modal-header border-bottom">
        <button className="btn btn-link text-dark p-0" onClick={onHide}>
          <i className="bi bi-chevron-left fs-4"></i>
        </button>
        <h5 className="mb-0 fw-bold">Create Course</h5>
        <div style={{ width: '24px' }}></div> {/* Spacer for centering */}
      </div>

      <Form onSubmit={handleSubmit} className="cd-modal-body bg-white p-4">
        {error && <div className="alert alert-danger">{error}</div>}

        <Form.Group className="mb-4">
          <Form.Label className="text-muted small mb-1">Course title</Form.Label>
          <Form.Control 
            type="text" 
            className="cd-input-gray border-0" 
            value={title} 
            onChange={e => setTitle(e.target.value)} 
            required 
          />
        </Form.Group>

        <Form.Group className="mb-4">
          <Form.Label className="text-muted small mb-1">Course Description</Form.Label>
          <Form.Control 
            as="textarea" 
            rows={5} 
            className="cd-input-gray border-0" 
            value={description} 
            onChange={e => setDescription(e.target.value)} 
            required 
          />
        </Form.Group>

        <Form.Group className="mb-4">
          <Form.Label className="text-muted small mb-1">Upload Video</Form.Label>
          <input 
            type="file" 
            accept="video/*" 
            ref={videoInputRef} 
            style={{ display: 'none' }} 
            onChange={e => setVideoFile(e.target.files[0])}
          />
          <Button 
            variant="outline-primary" 
            className="w-100 cd-btn-outline" 
            onClick={() => videoInputRef.current.click()}
          >
            {videoFile ? `Change (${videoFile.name})` : 'Add'}
          </Button>
        </Form.Group>

        <Form.Group className="mb-4">
          <Form.Label className="text-muted small mb-1">Add Video Link</Form.Label>
          <Form.Control 
            type="text" 
            placeholder="Paste HTTPS video URL (YouTube, Vimeo, etc.)"
            className="cd-input-gray border-0" 
            value={videoLink} 
            onChange={e => setVideoLink(e.target.value)} 
          />
        </Form.Group>

        <Form.Group className="mb-4">
          <Form.Label className="text-muted small mb-1">Upload PDF</Form.Label>
          <input 
            type="file" 
            accept=".pdf" 
            multiple
            ref={pdfInputRef} 
            style={{ display: 'none' }} 
            onChange={handlePdfUpload}
          />
          <Button 
            variant="outline-primary" 
            className="w-100 cd-btn-outline mb-2" 
            onClick={() => pdfInputRef.current.click()}
          >
            Add
          </Button>
          
          <div className="d-flex flex-column gap-2 mt-3">
            {pdfs.map(pdf => (
              <div key={pdf.id} className="cd-pdf-item d-flex justify-content-between align-items-center p-3 rounded-3 bg-light">
                <span className="text-truncate flex-grow-1" style={{ fontSize: '0.9rem' }}>{pdf.name}</span>
                <button 
                  type="button" 
                  className="btn btn-link text-danger p-0 ms-2" 
                  onClick={() => handleRemovePdf(pdf.id)}
                >
                  <i className="bi bi-trash-fill fs-5"></i>
                </button>
              </div>
            ))}
          </div>
        </Form.Group>

        <div className="mb-4">
          <Form.Label className="text-muted small mb-1">Add Questions</Form.Label>
          <Button 
            variant="outline-primary" 
            className="w-100 cd-btn-outline" 
            onClick={handleAddQuestion}
          >
            Add
          </Button>
        </div>

        <div className="d-flex flex-column gap-4 mb-5 pb-5">
          {questions.map((q, idx) => (
            <Card key={q.id} className="border-0 shadow-sm rounded-4">
              <Card.Body className="p-4">
                
                {q.isEditing ? (
                  <div>
                    <div className="d-flex justify-content-between align-items-center mb-3">
                      <span className="fw-bold">Edit Question {idx + 1}</span>
                      <button type="button" className="btn btn-link text-danger p-0" onClick={() => removeQuestion(q.id)}>
                        <i className="bi bi-trash-fill fs-5"></i>
                      </button>
                    </div>

                    <Form.Group className="mb-3">
                      <Form.Label className="small">Question Type</Form.Label>
                      <Form.Select 
                        className="cd-input-gray border-0" 
                        value={q.quizType} 
                        onChange={(e) => updateQuestion(q.id, 'quizType', e.target.value)}
                      >
                        <option value="paragraph">Text Answer</option>
                        <option value="singleChoice">Single Choice</option>
                        <option value="multipleChoice">Multiple Choice</option>
                      </Form.Select>
                    </Form.Group>

                    <Form.Group className="mb-3">
                      <Form.Label className="small">Question Text</Form.Label>
                      <Form.Control 
                        type="text" 
                        className="cd-input-gray border-0" 
                        value={q.question} 
                        onChange={(e) => updateQuestion(q.id, 'question', e.target.value)} 
                        placeholder="Write your question..."
                      />
                    </Form.Group>

                    {q.quizType === 'paragraph' && (
                      <Form.Group className="mb-3">
                        <Form.Label className="small">Correct Answer (Optional)</Form.Label>
                        <Form.Control 
                          type="text" 
                          className="cd-input-gray border-0" 
                          value={q.paragraphRightAnswer} 
                          onChange={(e) => updateQuestion(q.id, 'paragraphRightAnswer', e.target.value)} 
                          placeholder="Expected answer keywords..."
                        />
                      </Form.Group>
                    )}

                    {(q.quizType === 'singleChoice' || q.quizType === 'multipleChoice') && (
                      <div className="mb-3">
                        <Form.Label className="small">Options</Form.Label>
                        {q.options.map((opt, optIdx) => (
                          <div key={opt.id} className="d-flex align-items-center gap-2 mb-2">
                            <Form.Check 
                              type={q.quizType === 'singleChoice' ? 'radio' : 'checkbox'}
                              checked={opt.isCorrect}
                              onChange={(e) => updateOption(q.id, opt.id, 'isCorrect', e.target.checked)}
                              name={`correct-${q.id}`}
                              className="mb-0"
                            />
                            <Form.Control 
                              type="text" 
                              className="cd-input-gray border-0 flex-grow-1" 
                              value={opt.text} 
                              onChange={(e) => updateOption(q.id, opt.id, 'text', e.target.value)} 
                              placeholder={`Option ${optIdx + 1}`}
                            />
                            <button type="button" className="btn btn-link text-danger p-0" onClick={() => removeOption(q.id, opt.id)}>
                              <i className="bi bi-x-circle-fill"></i>
                            </button>
                          </div>
                        ))}
                        <Button variant="link" className="p-0 text-primary small mt-1" onClick={() => addOption(q.id)}>
                          + Add Option
                        </Button>
                      </div>
                    )}

                    <Button variant="primary" className="w-100 rounded-pill mt-2" onClick={() => toggleEditQuestion(q.id)}>
                      Done Editing
                    </Button>
                  </div>
                ) : (
                  <div>
                    <div className="d-flex justify-content-between align-items-start mb-3">
                      <h6 className="fw-bold mb-0 flex-grow-1 pe-3">
                        {idx + 1}. {q.question || <span className="text-muted fst-italic">Untitled Question</span>}
                      </h6>
                      <div className="d-flex gap-3">
                        <button type="button" className="btn btn-link text-primary p-0" onClick={() => toggleEditQuestion(q.id)}>
                          <i className="bi bi-pencil-fill fs-5"></i>
                        </button>
                        <button type="button" className="btn btn-link text-danger p-0" onClick={() => removeQuestion(q.id)}>
                          <i className="bi bi-trash-fill fs-5"></i>
                        </button>
                      </div>
                    </div>

                    {q.quizType === 'paragraph' && (
                      <div className="cd-input-gray rounded-3 p-3 text-muted">
                        Enter your answer
                      </div>
                    )}

                    {(q.quizType === 'singleChoice' || q.quizType === 'multipleChoice') && (
                      <div className="d-flex flex-column gap-3">
                        {q.options.map(opt => (
                          <div key={opt.id} className="d-flex align-items-center gap-3">
                            {q.quizType === 'singleChoice' ? (
                              <div className="cd-radio-circle"></div>
                            ) : (
                              <div className="cd-checkbox-square"></div>
                            )}
                            <span>{opt.text || <span className="text-muted">Empty Option</span>}</span>
                          </div>
                        ))}
                        <div className="mt-2 small" style={{ color: '#F39C12' }}>
                          Correct answer(s): {q.options.filter(o => o.isCorrect).map(o => o.text).join(', ') || 'None'}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </Card.Body>
            </Card>
          ))}
        </div>

        <div className="cd-modal-footer bg-white border-top p-3 position-fixed bottom-0 start-0 w-100">
          <Button variant="primary" type="submit" disabled={loading} className="w-100 rounded-pill cd-btn-blue border-0 py-3 fw-bold fs-6">
            {loading ? <Spinner size="sm" animation="border" /> : 'Post'}
          </Button>
        </div>
      </Form>
    </Modal>
  );
};
